import React, { useState } from "react";
import {
  DOADOR_FIGADO,
  ehDoadorFigado,
} from "../../../shared/utils/modalidadeAtendimento";
import {
  Plus,
  CheckCircle,
  User,
  Hospital,
  Trash2,
  MapPin,
  Phone,
  AlertTriangle,
  Clock,
  Save,
} from "lucide-react";
import { usePacientes } from "../context/PacientesContext";
import {
  TipoTransplante,
  StatusTransplante,
  TipoInterface,
} from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import {
  analisarTelefone,
  formatarTelefone,
} from "../../../shared/utils/telefoneBR";

// ============================================
// 🔢 FUNÇÕES DE CPF
// ============================================

// Formatar CPF: 123.456.789-00
const formatarCPF = (valor: string): string => {
  // Remove tudo que não é número
  const numeros = valor.replace(/\D/g, "");

  // Limita a 11 dígitos
  const numerosLimitados = numeros.slice(0, 11);

  // Aplica a máscara
  if (numerosLimitados.length <= 3) {
    return numerosLimitados;
  } else if (numerosLimitados.length <= 6) {
    return `${numerosLimitados.slice(0, 3)}.${numerosLimitados.slice(3)}`;
  } else if (numerosLimitados.length <= 9) {
    return `${numerosLimitados.slice(0, 3)}.${numerosLimitados.slice(
      3,
      6
    )}.${numerosLimitados.slice(6)}`;
  } else {
    return `${numerosLimitados.slice(0, 3)}.${numerosLimitados.slice(
      3,
      6
    )}.${numerosLimitados.slice(6, 9)}-${numerosLimitados.slice(9)}`;
  }
};

// Validar CPF (algoritmo oficial)
const validarCPF = (cpf: string): boolean => {
  // Remove caracteres não numéricos
  const numeros = cpf.replace(/\D/g, "");

  // Verifica se tem 11 dígitos
  if (numeros.length !== 11) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1{10}$/.test(numeros)) {
    return false;
  }

  // Calcula primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(numeros.charAt(i)) * (10 - i);
  }
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(numeros.charAt(9))) {
    return false;
  }

  // Calcula segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(numeros.charAt(i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(numeros.charAt(10))) {
    return false;
  }

  return true;
};

// Validar e limitar ano a 4 dígitos em campos de data
const limitarAnoData = (valor: string): string => {
  if (valor && valor.includes("-")) {
    const partes = valor.split("-");
    if (partes.length >= 1 && partes[0].length > 4) {
      partes[0] = partes[0].slice(0, 4);
      return partes.join("-");
    }
  }
  return valor;
};

// Validar se a data não é futura
// B296 — fonte única (fuso de Brasília). Retorna true se a data NÃO é futura.
const validarDataNaoFutura = (valor: string): boolean => !ehDataFutura(valor);

export default function CadastroPaciente() {
  const { pacientes, adicionarPaciente } = usePacientes();

  // Estado do formulário
  const [formData, setFormData] = useState({
    // Dados Pessoais
    nome: "",
    cpf: "",
    rg: "",
    cns: "",
    hospub: "",
    dataNascimento: "",
    sexo: "M" as "M" | "F",
    nomeMae: "",
    estadoCivil: "" as
      | ""
      | "solteiro"
      | "casado"
      | "divorciado"
      | "viuvo"
      | "uniao_estavel",
    profissao: "",

    // Endereço
    endereco: {
      rua: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      cep: "",
    },

    // Contatos
    telefone: "",
    telefone2: "",
    telefone3: "",
    email: "",

    // Contato de Emergência
    contatoEmergencia: {
      nome: "",
      parentesco: "",
      telefone: "",
    },

    // Dados Clínicos
    peso: "" as string | number,
    altura: "" as string | number,
    grupoSanguineo: "" as
      | ""
      | "A+"
      | "A-"
      | "B+"
      | "B-"
      | "AB+"
      | "AB-"
      | "O+"
      | "O-",
    alergias: "",
    medicacoesEmUso: "",
    comorbidades: "",

    // Tipo de Atendimento
    // B351 (AI_152) — o doador entra na união do formulário. Aqui o TypeScript
    // AJUDA (o campo é união literal, não string livre como em `Paciente`), então
    // esquecer um ramo vira erro de compilação em vez de comportamento silencioso.
    tipoAtendimento: "ambulatorial" as
      | "ambulatorial"
      | typeof DOADOR_FIGADO
      | TipoTransplante,
    statusTransplante: "" as "" | StatusTransplante,
    interfaceAtual: "ambulatorial" as TipoInterface,

    // ✅ NOVO: Dados de Inscrição (centralizado)
    inscricaoTx: {
      statusInscricao: "" as "" | "pre" | "lista_espera" | "pos",
      dataInscricao: "",
      numeroRGCT: "",
      situacaoEspecial: false,
      dataAprovacaoSE: "",
      tipoSE: "",
      dataTransplante: "",
      hospitalTransplante: "",
    },

    // ⚠️ DEPRECATED (manter por compatibilidade)
    centroTransplantador: "",
    equipeMedica: "",
    tipoDoador: "" as "" | "vivo" | "falecido",
    tempoIsquemia: "",
    numeroRGCT: "",
    dataInclusaoLista: "",
    dataTransplante: "",
  });

  const [erros, setErros] = useState<{ [key: string]: string }>({});
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  // Atualizar campo
  const handleChange = (campo: string, valor: any) => {
    let valorFinal = valor;

    // Formatação especial para CPF
    if (campo === "cpf") {
      valorFinal = formatarCPF(valor);
    }

    // Validação para campos de data - limita ano a 4 dígitos
    if (
      campo === "dataNascimento" ||
      campo === "dataInscricao" ||
      campo === "dataTransplante"
    ) {
      if (valor && valor.includes("-")) {
        const [ano, mes, dia] = valor.split("-");
        if (ano && ano.length > 4) {
          valorFinal = `${ano.slice(0, 4)}-${mes}-${dia}`;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [campo]: valorFinal,
    }));
    // Limpar erro do campo
    if (erros[campo]) {
      setErros((prev) => {
        const novosErros = { ...prev };
        delete novosErros[campo];
        return novosErros;
      });
    }
  };

  // Atualizar endereço
  const handleEnderecoChange = (campo: string, valor: string) => {
    setFormData((prev) => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        [campo]: valor,
      },
    }));
  };

  // Atualizar contato de emergência
  const handleContatoEmergenciaChange = (campo: string, valor: string) => {
    setFormData((prev) => ({
      ...prev,
      contatoEmergencia: {
        ...prev.contatoEmergencia,
        [campo]: valor,
      },
    }));
  };

  // Validar formulário
  const validarFormulario = (): boolean => {
    const novosErros: { [key: string]: string } = {};

    // Campos obrigatórios
    if (!formData.nome.trim()) {
      novosErros.nome = "Nome é obrigatório";
    }

    if (!formData.cpf.trim()) {
      novosErros.cpf = "CPF é obrigatório";
    } else if (!validarCPF(formData.cpf)) {
      novosErros.cpf = "CPF inválido";
    }

    if (!formData.dataNascimento) {
      novosErros.dataNascimento = "Data de nascimento é obrigatória";
    } else if (!validarDataNaoFutura(formData.dataNascimento)) {
      novosErros.dataNascimento = "Data de nascimento não pode ser futura";
    }

    if (!formData.telefone.trim()) {
      novosErros.telefone = "Telefone é obrigatório";
    }

    // Validar datas de inscrição e transplante (se preenchidas)
    if (
      formData.inscricaoTx?.dataInscricao &&
      !validarDataNaoFutura(formData.inscricaoTx.dataInscricao)
    ) {
      novosErros.dataInscricao = "Data de inscrição não pode ser futura";
    }
    if (
      formData.inscricaoTx?.dataTransplante &&
      !validarDataNaoFutura(formData.inscricaoTx.dataTransplante)
    ) {
      novosErros.dataTransplante = "Data de transplante não pode ser futura";
    }
    if (
      formData.inscricaoTx?.dataAprovacaoSE &&
      !validarDataNaoFutura(formData.inscricaoTx.dataAprovacaoSE)
    ) {
      novosErros.dataAprovacaoSE = "Data de aprovação SE não pode ser futura";
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };
  // Limpar formulário
  const limparFormulario = () => {
    setFormData({
      nome: "",
      cpf: "",
      rg: "",
      cns: "",
      hospub: "",
      dataNascimento: "",
      sexo: "M",
      nomeMae: "",
      estadoCivil: "",
      profissao: "",
      endereco: {
        rua: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        estado: "",
        cep: "",
      },
      telefone: "",
      telefone2: "",
      telefone3: "",
      email: "",
      contatoEmergencia: { nome: "", parentesco: "", telefone: "" },
      peso: "",
      altura: "",
      grupoSanguineo: "",
      alergias: "",
      medicacoesEmUso: "",
      comorbidades: "",
      tipoAtendimento: "ambulatorial",
      statusTransplante: "",
      interfaceAtual: TipoInterface.AMBULATORIAL,
      centroTransplantador: "",
      equipeMedica: "",
      tipoDoador: "",
      tempoIsquemia: "",
      numeroRGCT: "",
      dataInclusaoLista: "",
      dataTransplante: "",
      inscricaoTx: {
        statusInscricao: "",
        dataInscricao: "",
        numeroRGCT: "",
        situacaoEspecial: false,
        dataAprovacaoSE: "",
        tipoSE: "",
        dataTransplante: "",
        hospitalTransplante: "",
      },
    });
    setErros({});
  };
  // Salvar paciente
  const handleSalvar = () => {
    if (!validarFormulario()) {
      alert("❌ Por favor, preencha os campos obrigatórios");
      return;
    }

    // ✅ Verificar duplicatas antes de cadastrar
    const nomeNovo = formData.nome.trim().toLowerCase();
    const cpfNovo = formData.cpf.replace(/\D/g, "");

    // Verificar CPF duplicado (se preenchido)
    if (cpfNovo.length > 0) {
      const pacienteMesmoCPF = pacientes.find(
        (p: any) => p.cpf && p.cpf.replace(/\D/g, "") === cpfNovo
      );
      if (pacienteMesmoCPF) {
        alert(
          `❌ Já existe um paciente com este CPF:\n\n${pacienteMesmoCPF.nome}\nCPF: ${pacienteMesmoCPF.cpf}\n\nVerifique os dados antes de prosseguir.`
        );
        return;
      }
    }

    // Verificar nome duplicado
    const pacienteMesmoNome = pacientes.find(
      (p: any) => p.nome.trim().toLowerCase() === nomeNovo
    );
    if (pacienteMesmoNome) {
      const confirma = window.confirm(
        `⚠️ Já existe um paciente com nome idêntico:\n\n${
          pacienteMesmoNome.nome
        }\nCPF: ${
          pacienteMesmoNome.cpf || "Não informado"
        }\n\nDeseja cadastrar mesmo assim?`
      );
      if (!confirma) return;
    }

    setSalvando(true);

    try {
      // ✅ Determinar interfaceAtual baseado no tipo e status
      let interfaceAtual: TipoInterface;

      // FÍGADO - usar inscricaoTx.statusInscricao
      if (formData.tipoAtendimento === "figado") {
        if (formData.inscricaoTx.statusInscricao === "pos") {
          interfaceAtual = "figado_pos_tx" as TipoInterface;
        } else {
          interfaceAtual = "figado_pre_tx" as TipoInterface;
        }
      }
      // RIM - usar statusTransplante
      else if (formData.tipoAtendimento === "rim") {
        if (formData.statusTransplante === "pos") {
          interfaceAtual = "rim_pos_tx" as TipoInterface;
        } else {
          interfaceAtual = "rim_pre_tx" as TipoInterface;
        }
      }
      // PÂNCREAS - usar statusTransplante
      else if (formData.tipoAtendimento === "pancreas") {
        if (formData.statusTransplante === "pos") {
          interfaceAtual = "pancreas_pos_tx" as TipoInterface;
        } else {
          interfaceAtual = "pancreas_pre_tx" as TipoInterface;
        }
      }
      // RIM-PÂNCREAS - usar statusTransplante
      else if (formData.tipoAtendimento === "rim_pancreas") {
        if (formData.statusTransplante === "pos") {
          interfaceAtual = "rim_pancreas_pos_tx" as TipoInterface;
        } else {
          interfaceAtual = "rim_pancreas_pre_tx" as TipoInterface;
        }
      }
      // B351 (AI_152) — DOADOR de fígado: não tem pré/pós-transplante (ele não é
      // transplantado; doa um lobo). Interface própria para não herdar o fluxo
      // do receptor.
      else if (formData.tipoAtendimento === DOADOR_FIGADO) {
        interfaceAtual = "doador_figado" as TipoInterface;
      }
      // AMBULATORIAL - sem status
      else {
        interfaceAtual = "ambulatorial" as TipoInterface;
      }

      const novoPaciente = {
        nome: formData.nome,
        cpf: formData.cpf,
        rg: formData.rg || undefined,
        cns: formData.cns || undefined,
        hospub: formData.hospub || undefined,
        dataNascimento: formData.dataNascimento,
        sexo: formData.sexo,
        nomeMae: formData.nomeMae || undefined,
        estadoCivil: formData.estadoCivil || undefined,
        profissao: formData.profissao || undefined,

        endereco: formData.endereco.rua ? formData.endereco : undefined,

        telefone: formData.telefone,
        telefone2: formData.telefone2 || undefined,
        telefone3: formData.telefone3 || undefined,
        email: formData.email || undefined,

        contatoEmergencia: formData.contatoEmergencia.nome
          ? formData.contatoEmergencia
          : undefined,

        peso: formData.peso ? Number(formData.peso) : undefined,
        altura: formData.altura ? Number(formData.altura) : undefined,
        grupoSanguineo: formData.grupoSanguineo || undefined,
        alergias: formData.alergias || undefined,
        medicacoesEmUso: formData.medicacoesEmUso || undefined,
        comorbidades: formData.comorbidades || undefined,

        tipoAtendimento: formData.tipoAtendimento,
        statusTransplante: (formData.statusTransplante ||
          "pre") as StatusTransplante,
        interfaceAtual: interfaceAtual,

        // ✅ NOVO: Dados de inscrição centralizados (APENAS PARA FÍGADO)
        inscricaoTx:
          formData.tipoAtendimento === "figado" &&
          formData.inscricaoTx.statusInscricao
            ? {
                statusInscricao: formData.inscricaoTx.statusInscricao,
                dataInscricao: formData.inscricaoTx.dataInscricao || undefined,
                numeroRGCT: formData.inscricaoTx.numeroRGCT || undefined,
                situacaoEspecial:
                  formData.inscricaoTx.situacaoEspecial || false,
                dataAprovacaoSE:
                  formData.inscricaoTx.dataAprovacaoSE || undefined,
                tipoSE: formData.inscricaoTx.tipoSE || undefined,
                dataTransplante:
                  formData.inscricaoTx.dataTransplante || undefined,
                hospitalTransplante:
                  formData.inscricaoTx.hospitalTransplante || undefined,
              }
            : undefined,

        // ✅ Campos planos sincronizados com inscricaoTx (para Dashboard e IA)
        dataInscricao:
          formData.inscricaoTx.dataInscricao ||
          formData.dataInclusaoLista ||
          undefined,
        situacaoEspecial: formData.inscricaoTx.situacaoEspecial || false,
        dataAprovacaoSE: formData.inscricaoTx.dataAprovacaoSE || undefined,
        tipoSE: formData.inscricaoTx.tipoSE || undefined,
        numeroRGCT:
          formData.inscricaoTx.numeroRGCT || formData.numeroRGCT || undefined,
        dataTransplante:
          formData.inscricaoTx.dataTransplante ||
          formData.dataTransplante ||
          undefined,
        hospitalTransplante:
          formData.inscricaoTx.hospitalTransplante ||
          formData.centroTransplantador ||
          undefined,
        // ⚠️ DEPRECATED (manter por compatibilidade)
        centroTransplantador: formData.centroTransplantador || undefined,
        equipeMedica: formData.equipeMedica || undefined,
        tipoDoador: formData.tipoDoador || undefined,
        tempoIsquemia: formData.tempoIsquemia || undefined,
        dataInclusaoLista: formData.dataInclusaoLista || undefined,
      };

      adicionarPaciente(novoPaciente);
      setSalvando(false);

      // MOSTRAR ALERTA DE SUCESSO
      alert("✅ Paciente cadastrado com sucesso!");

      // Limpar formulário
      limparFormulario();
    } catch (error) {
      setSalvando(false);
      alert("❌ Erro ao cadastrar paciente");
      console.error(error);
    }
  };

  // Estilos
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px",
    border: `2px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: "14px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box",
  };

  const inputErrorStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: "#ef4444",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: theme.gray[700],
    marginBottom: "6px",
    fontFamily: theme.fonts.sans,
  };

  const sectionStyle: React.CSSProperties = {
    background: "white",
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.md,
    padding: "24px",
    marginBottom: "24px",
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: "20px",
    fontWeight: "bold",
    color: theme.gray[800],
    marginBottom: "20px",
    fontFamily: theme.fonts.sans,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
  };

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* HEADER */}
      <div
        style={{
          background: theme.gradients.header,
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          padding: "24px",
          marginBottom: "24px",
          color: "white",
        }}
      >
        <h2
          style={{
            fontSize: "28px",
            fontWeight: "bold",
            margin: "0 0 8px 0",
            fontFamily: theme.fonts.sans,
          }}
        >
          <Plus size={16} style={{ marginRight: "8px" }} /> Cadastrar Novo
          Paciente
        </h2>
        <p style={{ margin: 0, fontSize: "14px", opacity: 0.9 }}>
          Preencha os dados do paciente. Campos com * são obrigatórios.
        </p>
      </div>
      {/* MENSAGEM DE SUCESSO */}
      {sucesso && (
        <div
          style={{
            background: "#d1fae5",
            border: "2px solid #10b981",
            color: "#064e3b",
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            marginBottom: "24px",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <CheckCircle size={24} color="#10b981" />
          <span>Paciente cadastrado com sucesso!</span>
        </div>
      )}
      {/* SEÇÃO 1: DADOS PESSOAIS */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <User size={16} style={{ marginRight: "8px" }} /> Dados Pessoais
        </h3>

        <div style={gridStyle}>
          {/* NOME */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>
              Nome Completo <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => handleChange("nome", e.target.value)}
              style={erros.nome ? inputErrorStyle : inputStyle}
              placeholder="Nome completo do paciente"
            />
            {erros.nome && (
              <p
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {erros.nome}
              </p>
            )}
          </div>

          {/* CPF */}
          <div>
            <label style={labelStyle}>
              CPF <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              value={formData.cpf}
              onChange={(e) => handleChange("cpf", e.target.value)}
              style={erros.cpf ? inputErrorStyle : inputStyle}
              placeholder="000.000.000-00"
              maxLength={14}
            />
            {erros.cpf && (
              <p
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {erros.cpf}
              </p>
            )}
          </div>

          {/* RG */}
          <div>
            <label style={labelStyle}>RG</label>
            <input
              type="text"
              value={formData.rg}
              onChange={(e) => handleChange("rg", e.target.value)}
              style={inputStyle}
              placeholder="00.000.000-0"
            />
          </div>

          {/* CNS */}
          <div>
            <label style={labelStyle}>CNS (Cartão Nacional de Saúde)</label>
            <input
              type="text"
              value={formData.cns}
              onChange={(e) => handleChange("cns", e.target.value)}
              style={inputStyle}
              placeholder="000 0000 0000 0000"
            />
          </div>

          {/* HOSPUB */}
          <div>
            <label style={labelStyle}>HOSPUB (Registro SUS)</label>
            <input
              type="text"
              value={formData.hospub}
              onChange={(e) => handleChange("hospub", e.target.value)}
              style={inputStyle}
              placeholder="Número do HOSPUB"
            />
          </div>

          {/* DATA NASCIMENTO */}
          <div>
            <label style={labelStyle}>
              Data de Nascimento <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="date"
              value={formData.dataNascimento}
              max={hojeISO()}
              onChange={(e) => handleChange("dataNascimento", e.target.value)}
              style={erros.dataNascimento ? inputErrorStyle : inputStyle}
            />
            {erros.dataNascimento && (
              <p
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {erros.dataNascimento}
              </p>
            )}
          </div>

          {/* SEXO */}
          <div>
            <label style={labelStyle}>
              Sexo <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              value={formData.sexo}
              onChange={(e) => handleChange("sexo", e.target.value)}
              style={inputStyle}
            >
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
            </select>
          </div>

          {/* NOME DA MÃE */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Nome da Mãe</label>
            <input
              type="text"
              value={formData.nomeMae}
              onChange={(e) => handleChange("nomeMae", e.target.value)}
              style={inputStyle}
              placeholder="Nome completo da mãe"
            />
          </div>

          {/* ESTADO CIVIL */}
          <div>
            <label style={labelStyle}>Estado Civil</label>
            <select
              value={formData.estadoCivil}
              onChange={(e) => handleChange("estadoCivil", e.target.value)}
              style={inputStyle}
            >
              <option value="">Selecione...</option>
              <option value="solteiro">Solteiro(a)</option>
              <option value="casado">Casado(a)</option>
              <option value="divorciado">Divorciado(a)</option>
              <option value="viuvo">Viúvo(a)</option>
              <option value="uniao_estavel">União Estável</option>
            </select>
          </div>

          {/* PROFISSÃO */}
          <div>
            <label style={labelStyle}>Profissão</label>
            <input
              type="text"
              value={formData.profissao}
              onChange={(e) => handleChange("profissao", e.target.value)}
              style={inputStyle}
              placeholder="Profissão"
            />
          </div>
        </div>
      </div>
      {/* SEÇÃO 2: ENDEREÇO */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <MapPin size={16} style={{ marginRight: "8px" }} /> Endereço
        </h3>

        <div style={gridStyle}>
          {/* RUA */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Rua/Avenida</label>
            <input
              type="text"
              value={formData.endereco.rua}
              onChange={(e) => handleEnderecoChange("rua", e.target.value)}
              style={inputStyle}
              placeholder="Nome da rua"
            />
          </div>

          {/* NÚMERO */}
          <div>
            <label style={labelStyle}>Número</label>
            <input
              type="text"
              value={formData.endereco.numero}
              onChange={(e) => handleEnderecoChange("numero", e.target.value)}
              style={inputStyle}
              placeholder="Nº"
            />
          </div>

          {/* COMPLEMENTO */}
          <div>
            <label style={labelStyle}>Complemento</label>
            <input
              type="text"
              value={formData.endereco.complemento}
              onChange={(e) =>
                handleEnderecoChange("complemento", e.target.value)
              }
              style={inputStyle}
              placeholder="Apto, sala, etc"
            />
          </div>

          {/* BAIRRO */}
          <div>
            <label style={labelStyle}>Bairro</label>
            <input
              type="text"
              value={formData.endereco.bairro}
              onChange={(e) => handleEnderecoChange("bairro", e.target.value)}
              style={inputStyle}
              placeholder="Bairro"
            />
          </div>

          {/* CIDADE */}
          <div>
            <label style={labelStyle}>Cidade</label>
            <input
              type="text"
              value={formData.endereco.cidade}
              onChange={(e) => handleEnderecoChange("cidade", e.target.value)}
              style={inputStyle}
              placeholder="Cidade"
            />
          </div>

          {/* ESTADO */}
          <div>
            <label style={labelStyle}>Estado</label>
            <input
              type="text"
              value={formData.endereco.estado}
              onChange={(e) => handleEnderecoChange("estado", e.target.value)}
              style={inputStyle}
              placeholder="UF"
              maxLength={2}
            />
          </div>

          {/* CEP */}
          <div>
            <label style={labelStyle}>CEP</label>
            <input
              type="text"
              value={formData.endereco.cep}
              onChange={(e) => handleEnderecoChange("cep", e.target.value)}
              style={inputStyle}
              placeholder="00000-000"
              maxLength={9}
            />
          </div>
        </div>
      </div>
      {/* SEÇÃO 3: CONTATOS */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <Phone size={16} style={{ marginRight: "8px" }} /> Contatos
        </h3>

        <div style={gridStyle}>
          {/* TELEFONE 1 */}
          <div>
            <label style={labelStyle}>
              Telefone 1 <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="tel"
              value={formData.telefone}
              onChange={(e) => handleChange("telefone", e.target.value)}
              // B345 — formata ao SAIR do campo (durante a digitação atrapalharia).
              onBlur={(e) => handleChange("telefone", formatarTelefone(e.target.value))}
              style={erros.telefone ? inputErrorStyle : inputStyle}
              placeholder="(00) 00000-0000"
            />
            {erros.telefone && (
              <p
                style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
              >
                {erros.telefone}
              </p>
            )}
            {/* B345 (AI_150) — este é o telefone que recebe a receita por
                WhatsApp. Avisa NA HORA quando o número não vai conseguir
                receber (fixo, sem DDD, DDD inexistente). Não bloqueia: o
                cadastro é do médico; só evita descobrir dias depois que o
                paciente nunca recebeu. */}
            {!erros.telefone &&
              (() => {
                const an = analisarTelefone(formData.telefone);
                if (!an.aviso) return null;
                return (
                  <p
                    style={{
                      color: "#b45309",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    ⚠️ {an.aviso}
                  </p>
                );
              })()}
          </div>

          {/* TELEFONE 2 */}
          <div>
            <label style={labelStyle}>Telefone 2</label>
            <input
              type="tel"
              value={formData.telefone2}
              onChange={(e) => handleChange("telefone2", e.target.value)}
              style={inputStyle}
              placeholder="(00) 00000-0000"
            />
          </div>

          {/* TELEFONE 3 */}
          <div>
            <label style={labelStyle}>Telefone 3</label>
            <input
              type="tel"
              value={formData.telefone3}
              onChange={(e) => handleChange("telefone3", e.target.value)}
              style={inputStyle}
              placeholder="(00) 00000-0000"
            />
          </div>

          {/* EMAIL */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              style={inputStyle}
              placeholder="email@exemplo.com"
            />
          </div>
        </div>

        {/* CONTATO DE EMERGÊNCIA */}
        <div style={{ marginTop: "24px" }}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: theme.gray[800],
              marginBottom: "16px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <AlertTriangle size={16} style={{ marginRight: "8px" }} /> Contato
            de Emergência
          </h4>

          <div style={gridStyle}>
            {/* NOME DO CONTATO */}
            <div>
              <label style={labelStyle}>Nome do Contato</label>
              <input
                type="text"
                value={formData.contatoEmergencia.nome}
                onChange={(e) =>
                  handleContatoEmergenciaChange("nome", e.target.value)
                }
                style={inputStyle}
                placeholder="Nome completo"
              />
            </div>

            {/* PARENTESCO */}
            <div>
              <label style={labelStyle}>Parentesco</label>
              <input
                type="text"
                value={formData.contatoEmergencia.parentesco}
                onChange={(e) =>
                  handleContatoEmergenciaChange("parentesco", e.target.value)
                }
                style={inputStyle}
                placeholder="Ex: Esposa, Filho, Irmão"
              />
            </div>

            {/* TELEFONE DO CONTATO */}
            <div>
              <label style={labelStyle}>Telefone do Contato</label>
              <input
                type="tel"
                value={formData.contatoEmergencia.telefone}
                onChange={(e) =>
                  handleContatoEmergenciaChange("telefone", e.target.value)
                }
                style={inputStyle}
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>
        </div>
      </div>
      {/* SEÇÃO 4: DADOS CLÍNICOS */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <Hospital size={16} style={{ marginRight: "8px" }} /> Dados Clínicos
        </h3>

        <div style={gridStyle}>
          {/* PESO */}
          <div>
            <label style={labelStyle}>Peso (kg)</label>
            <input
              type="number"
              step="0.1"
              value={formData.peso}
              onChange={(e) => handleChange("peso", e.target.value)}
              style={inputStyle}
              placeholder="70.5"
            />
          </div>

          {/* ALTURA */}
          <div>
            <label style={labelStyle}>Altura (cm)</label>
            <input
              type="number"
              value={formData.altura}
              onChange={(e) => handleChange("altura", e.target.value)}
              style={inputStyle}
              placeholder="175"
            />
          </div>

          {/* TIPO SANGUÍNEO */}
          <div>
            <label style={labelStyle}>Tipo Sanguíneo</label>
            <select
              value={formData.grupoSanguineo}
              onChange={(e) => handleChange("grupoSanguineo", e.target.value)}
              style={inputStyle}
            >
              <option value="">Selecione...</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>

          {/* ALERGIAS */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Alergias</label>
            <textarea
              value={formData.alergias}
              onChange={(e) => handleChange("alergias", e.target.value)}
              style={{
                ...inputStyle,
                minHeight: "80px",
                resize: "vertical",
              }}
              placeholder="Descreva alergias medicamentosas, alimentares ou outras..."
            />
          </div>

          {/* MEDICAÇÕES EM USO */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Medicações em Uso</label>
            <textarea
              value={formData.medicacoesEmUso}
              onChange={(e) => handleChange("medicacoesEmUso", e.target.value)}
              style={{
                ...inputStyle,
                minHeight: "80px",
                resize: "vertical",
              }}
              placeholder="Liste as medicações em uso contínuo..."
            />
          </div>

          {/* COMORBIDADES */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Comorbidades</label>
            <textarea
              value={formData.comorbidades}
              onChange={(e) => handleChange("comorbidades", e.target.value)}
              style={{
                ...inputStyle,
                minHeight: "80px",
                resize: "vertical",
              }}
              placeholder="Hipertensão, Diabetes, etc..."
            />
          </div>
        </div>
      </div>
      {/* SEÇÃO 5: TIPO DE ATENDIMENTO */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <Hospital size={16} style={{ marginRight: "8px" }} /> Tipo de
          Atendimento
        </h3>
        <div style={gridStyle}>
          {/* TIPO DE ATENDIMENTO */}
          <div>
            <label style={labelStyle}>
              Tipo de Atendimento <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              value={formData.tipoAtendimento}
              onChange={(e) => handleChange("tipoAtendimento", e.target.value)}
              style={inputStyle}
            >
              <option value="ambulatorial">Ambulatorial</option>
              <option value="figado">Transplante de Fígado</option>
              <option value="rim">Transplante de Rim</option>
              <option value="pancreas">Transplante de Pâncreas</option>
              {/* B351 (AI_152) — 5ª modalidade: o DOADOR vivo, não o receptor. */}
              <option value="doador_figado">
                Doador de Fígado (intervivos)
              </option>
            </select>
          </div>

          {/* STATUS TRANSPLANTE - SÓ PARA FÍGADO */}
          {formData.tipoAtendimento === "figado" && (
            <div>
              <label style={labelStyle}>
                Status do Paciente <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* PRÉ-TRANSPLANTE */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "12px",
                    border: `2px solid ${
                      formData.inscricaoTx.statusInscricao === "pre"
                        ? theme.primary[500]
                        : theme.gray[300]
                    }`,
                    borderRadius: "8px",
                    background:
                      formData.inscricaoTx.statusInscricao === "pre"
                        ? `${theme.primary[50]}`
                        : "white",
                  }}
                >
                  <input
                    type="radio"
                    name="statusInscricao"
                    value="pre"
                    checked={formData.inscricaoTx.statusInscricao === "pre"}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          statusInscricao: "pre",
                          dataInscricao: "",
                          numeroRGCT: "",
                          dataTransplante: "",
                          hospitalTransplante: "",
                        },
                      }));
                    }}
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  />
                  <div>
                    <strong style={{ fontFamily: theme.fonts.sans }}>
                      Pré-Transplante
                    </strong>
                    <div
                      style={{
                        fontSize: "13px",
                        color: theme.gray[600],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Paciente em avaliação ou acompanhamento pré-tx
                    </div>
                  </div>
                </label>

                {/* LISTA DE ESPERA */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "12px",
                    border: `2px solid ${
                      formData.inscricaoTx.statusInscricao === "lista_espera"
                        ? theme.primary[500]
                        : theme.gray[300]
                    }`,
                    borderRadius: "8px",
                    background:
                      formData.inscricaoTx.statusInscricao === "lista_espera"
                        ? `${theme.primary[50]}`
                        : "white",
                  }}
                >
                  <input
                    type="radio"
                    name="statusInscricao"
                    value="lista_espera"
                    checked={
                      formData.inscricaoTx.statusInscricao === "lista_espera"
                    }
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          statusInscricao: "lista_espera",
                          dataTransplante: "",
                          hospitalTransplante: "",
                        },
                      }));
                    }}
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  />
                  <div>
                    <strong style={{ fontFamily: theme.fonts.sans }}>
                      Lista de Espera
                    </strong>
                    <div
                      style={{
                        fontSize: "13px",
                        color: theme.gray[600],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Paciente inscrito aguardando transplante
                    </div>
                  </div>
                </label>

                {/* PÓS-TRANSPLANTE */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "12px",
                    border: `2px solid ${
                      formData.inscricaoTx.statusInscricao === "pos"
                        ? theme.primary[500]
                        : theme.gray[300]
                    }`,
                    borderRadius: "8px",
                    background:
                      formData.inscricaoTx.statusInscricao === "pos"
                        ? `${theme.primary[50]}`
                        : "white",
                  }}
                >
                  <input
                    type="radio"
                    name="statusInscricao"
                    value="pos"
                    checked={formData.inscricaoTx.statusInscricao === "pos"}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          statusInscricao: "pos",
                          dataInscricao: "",
                          numeroRGCT: "",
                        },
                      }));
                    }}
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  />
                  <div>
                    <strong style={{ fontFamily: theme.fonts.sans }}>
                      Pós-Transplante
                    </strong>
                    <div
                      style={{
                        fontSize: "13px",
                        color: theme.gray[600],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Paciente já transplantado
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* CAMPOS CONDICIONAIS - LISTA DE ESPERA */}
          {formData.tipoAtendimento === "figado" &&
            formData.inscricaoTx.statusInscricao === "lista_espera" && (
              <>
                <div>
                  <label style={labelStyle}>
                    Data de Inscrição{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.inscricaoTx.dataInscricao}
                    max={hojeISO()}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          dataInscricao: limitarAnoData(e.target.value),
                        },
                      }))
                    }
                    style={erros.dataInscricao ? inputErrorStyle : inputStyle}
                  />
                  {erros.dataInscricao && (
                    <p
                      style={{
                        color: "#ef4444",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {erros.dataInscricao}
                    </p>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>
                    Número RGCT <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.inscricaoTx.numeroRGCT}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          numeroRGCT: e.target.value,
                        },
                      }))
                    }
                    style={inputStyle}
                    placeholder="Ex: 12345"
                  />
                </div>
              </>
            )}

          {/* CAMPOS CONDICIONAIS - PÓS-TRANSPLANTE */}
          {formData.tipoAtendimento === "figado" &&
            formData.inscricaoTx.statusInscricao === "pos" && (
              <>
                <div>
                  <label style={labelStyle}>
                    Data do Transplante{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.inscricaoTx.dataTransplante}
                    max={hojeISO()}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          dataTransplante: limitarAnoData(e.target.value),
                        },
                      }))
                    }
                    style={erros.dataTransplante ? inputErrorStyle : inputStyle}
                  />
                  {erros.dataTransplante && (
                    <p
                      style={{
                        color: "#ef4444",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {erros.dataTransplante}
                    </p>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>
                    Centro Transplantador{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.inscricaoTx.hospitalTransplante}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inscricaoTx: {
                          ...prev.inscricaoTx,
                          hospitalTransplante: e.target.value,
                        },
                      }))
                    }
                    style={inputStyle}
                    placeholder="Ex: Hospital das Clínicas - FMUSP"
                  />
                </div>
              </>
            )}

          {/* STATUS TRANSPLANTE - OUTROS ÓRGÃOS (manter como estava) */}
          {/* B351 (AI_152) — o DOADOR é excluído aqui de propósito: este bloco
              pega "tudo que não é ambulatorial nem fígado", e o doador cairia
              dentro ganhando um seletor "Pré/Pós-Transplante" que não se aplica
              a ele (ele doa, não recebe). */}
          {formData.tipoAtendimento !== "ambulatorial" &&
            formData.tipoAtendimento !== "figado" &&
            !ehDoadorFigado(formData.tipoAtendimento) && (
              <div>
                <label style={labelStyle}>Status do Transplante</label>
                <select
                  value={formData.statusTransplante}
                  onChange={(e) =>
                    handleChange("statusTransplante", e.target.value)
                  }
                  style={inputStyle}
                >
                  <option value="">Selecione...</option>
                  <option value="pre">Pré-Transplante</option>
                  <option value="lista">Lista de Espera</option>
                  <option value="pos">Pós-Transplante</option>
                </select>
              </div>
            )}
        </div>{" "}
        {/* ✅ ADICIONAR - Fecha gridStyle */}
      </div>{" "}
      {/* ✅ ADICIONAR - Fecha sectionStyle */}
      {/* BOTÕES DE AÇÃO */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          justifyContent: "flex-end",
          marginTop: "32px",
          marginBottom: "32px",
        }}
      >
        <button
          onClick={() => {
            if (window.confirm("Tem certeza que deseja limpar o formulário?")) {
              limparFormulario();
            }
          }}
          style={{
            padding: "14px 32px",
            background: theme.gray[200],
            color: theme.gray[700],
            border: "none",
            borderRadius: theme.borderRadius.lg,
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            fontFamily: theme.fonts.sans,
            transition: theme.transitions.normal,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = theme.gray[300];
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = theme.gray[200];
          }}
        >
          <Trash2 size={14} style={{ marginRight: "6px" }} /> Limpar Formulário
        </button>

        <button
          onClick={handleSalvar}
          disabled={salvando}
          style={{
            padding: "14px 32px",
            background: salvando ? theme.gray[400] : theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.lg,
            fontSize: "16px",
            fontWeight: "600",
            cursor: salvando ? "not-allowed" : "pointer",
            fontFamily: theme.fonts.sans,
            transition: theme.transitions.normal,
          }}
          onMouseOver={(e) => {
            if (!salvando) {
              e.currentTarget.style.background = theme.primary[700];
            }
          }}
          onMouseOut={(e) => {
            if (!salvando) {
              e.currentTarget.style.background = theme.primary[600];
            }
          }}
        >
          {salvando ? (
            <>
              <Clock size={14} style={{ marginRight: "6px" }} /> Cadastrando...
            </>
          ) : (
            <>
              <Save size={14} style={{ marginRight: "6px" }} /> Cadastrar
              Paciente
            </>
          )}
        </button>
      </div>
    </div>
  );
}
